import {
    ICorporationResponse,
    ILocationIssueSummaryResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useCorporationIssuesSummary } from 'custom_hooks/data_hooks/corporation/useCorporationIssuesSummary';
import { CorporationIssuesTable } from 'pages/Corporation/issues/CorporationIssuesTable';
import { Container, HeaderContainer, Title } from 'pages/Corporation/issues/styled';
import { getSortFunction, SortField } from 'pages/Corporation/issues/utils';
import { useCorporationOverviewStore } from 'pages/Corporation/state/CorporationOverviewStoreProvider';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { OverviewStatusLozenge } from 'shared/lozenge/OverviewStatusLozenge';

export type CorporationIssuesWidgetProps = {
    corporation: ICorporationResponse;
    filters?: CorporationOverviewFilters;
};

export type CorporationIssuesWidgetRef = {
    refetch: () => Promise<unknown>;
};

function CorporationIssuesWidget(
    { corporation, filters }: CorporationIssuesWidgetProps,
    ref: React.ForwardedRef<CorporationIssuesWidgetRef>
) {
    const { sort, setIssuesWidgetSort } = useCorporationOverviewStore((state) => ({
        sort: state.issuesWidgetSort,
        setIssuesWidgetSort: state.setIssuesWidgetSort,
    }));

    const { data, isFetching, isLoading, refetch } = useCorporationIssuesSummary({
        corporationId: corporation.Id,
        filters,
    });

    useImperativeHandle(ref, () => ({
        refetch,
    }));

    const handleSort = (field: SortField) => {
        if (sort.field === field) {
            if (sort.direction === 'asc') {
                setIssuesWidgetSort({ field: 'newIssueCount', direction: 'desc' });
            } else {
                setIssuesWidgetSort({ field, direction: 'asc' });
            }
        } else {
            setIssuesWidgetSort({ field, direction: 'desc' });
        }
    };

    const sortedData = useMemo<ILocationIssueSummaryResponse[]>(() => {
        if (!data) {
            return [];
        }
        return [...data].sort(getSortFunction(sort.field, sort.direction));
    }, [data, sort]);

    return (
        <Container>
            <HeaderContainer>
                <Title>Issues</Title>
                <OverviewStatusLozenge>PAST 2 WEEKS</OverviewStatusLozenge>
            </HeaderContainer>
            <CorporationIssuesTable
                sort={sort}
                onSort={handleSort}
                data={sortedData}
                isLoading={isLoading}
                isFetching={isFetching}
            />
        </Container>
    );
}

const memoCorporationIssuesWidget = memo(
    forwardRef<CorporationIssuesWidgetRef, CorporationIssuesWidgetProps>(
        CorporationIssuesWidget
    )
);
export { memoCorporationIssuesWidget as CorporationIssuesWidget };
